import { isRecord } from './data/contracts';

export type FavouriteOffer = { id: string };
export type FavouriteShop = { id: string };
export type InterruptShop = { id: string; dateAdded?: string };

export type StoredUser = {
  collectorNumber: string;
  offers: FavouriteOffer[];
  shops: FavouriteShop[];
  interruptShops: InterruptShop[];
};

export type VisitInfo = {
  firstName: string;
  cashBalance: number;
  dreamBalance: number;
  balance: number;
  tier?: string;
  blueRewardsTier?: string;
};

export type SessionUser = {
  accessToken: string;
  collectorNumber: string;
  encryptedCollectorNumber: string;
  visitInfo: VisitInfo;
};

export type UserApiResponse = StoredUser & {
  visitInfo: VisitInfo;
  authExpiry?: string | Date;
  ppid: string;
  accessToken?: string;
};

export type UserData = Partial<UserApiResponse>;

function hasFavouriteId(value: unknown): value is { id: string } {
  return isRecord(value) && typeof value.id === 'string';
}

function isStoredOffer(value: unknown): value is FavouriteOffer {
  return hasFavouriteId(value);
}

function isStoredShop(value: unknown): value is FavouriteShop {
  return hasFavouriteId(value);
}

function isInterrupt(value: unknown): value is InterruptShop {
  return isRecord(value) && typeof value.id === 'string'
    && (value.dateAdded === undefined || typeof value.dateAdded === 'string');
}

export function parseFavourite(value: unknown, kind: 'offer'): FavouriteOffer;
export function parseFavourite(value: unknown, kind: 'shop'): FavouriteShop;
export function parseFavourite(value: unknown, kind: 'interrupt'): InterruptShop;
export function parseFavourite(value: unknown, kind: 'offer' | 'shop' | 'interrupt') {
  let valid: boolean;
  if (kind === 'offer') {
    valid = hasFavouriteId(value) && Object.keys(value).length === 1;
  } else if (kind === 'shop') {
    valid = hasFavouriteId(value) && Object.keys(value).length === 1;
  } else {
    valid = isInterrupt(value) && typeof value.dateAdded === 'string';
  }
  if (!valid) throw new TypeError(`Invalid ${kind} favourite`);
  return value;
}

export function parseStoredUser(value: unknown): StoredUser {
  if (!isRecord(value) || typeof value.collectorNumber !== 'string'
    || !Array.isArray(value.offers) || !value.offers.every(isStoredOffer)
    || !Array.isArray(value.shops) || !value.shops.every(isStoredShop)
    || (value.interruptShops !== undefined
      && (!Array.isArray(value.interruptShops) || !value.interruptShops.every(isInterrupt)))) {
    throw new TypeError('Invalid user database record');
  }
  return {
    collectorNumber: value.collectorNumber,
    offers: value.offers,
    shops: value.shops,
    interruptShops: value.interruptShops || [],
  };
}

function parseVisitInfo(value: unknown): VisitInfo | null {
  if (!isRecord(value)) return null;

  const {
    firstName,
    cashBalance,
    dreamBalance,
    balance, tier,
    blueRewardsTier,
  } = value;

  if (
    typeof firstName !== 'string'
    || typeof cashBalance !== 'number'
    || typeof dreamBalance !== 'number'
    || typeof balance !== 'number'
    || (tier !== undefined && typeof tier !== 'string')
    || (blueRewardsTier !== undefined && typeof blueRewardsTier !== 'string')
  ) {
    return null;
  }

  return {
    firstName,
    cashBalance,
    dreamBalance,
    balance,
    ...(typeof tier === 'string' && { tier }),
    ...(typeof blueRewardsTier === 'string' && { blueRewardsTier }),
  };
}

export function parseUserData(value: unknown): UserApiResponse {
  if (!isRecord(value)) throw new TypeError('Invalid user API response');
  const stored = parseStoredUser(value);
  const visitInfo = parseVisitInfo(value.visitInfo);
  if (!visitInfo) throw new TypeError('Invalid visit info');
  const { authExpiry, ppid, accessToken } = value;
  if (typeof ppid !== 'string' || !ppid) throw new TypeError('Invalid publisher ID');
  const user: UserApiResponse = { ...stored, visitInfo, ppid };
  if (authExpiry !== undefined && authExpiry !== null) {
    if (typeof authExpiry !== 'string' && !(authExpiry instanceof Date)) throw new TypeError('Invalid auth expiry');
    user.authExpiry = authExpiry;
  }
  if (accessToken !== undefined) {
    if (typeof accessToken !== 'string') throw new TypeError('Invalid access token');
    user.accessToken = accessToken;
  }
  return user;
}
